import { useEffect, useRef, useState, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { lightBlue, blue } from '@mui/material/colors';
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { cloneDeep, findIndex, isEmpty, isEqual, isNull, isUndefined } from "lodash";

import rpWorkbenchService from "@plannel/services/rp/rp-workbench-service";
import planVersionService from "@plannel/services/data/plan-version-service";

import SplitterContainer from "@plannel/components/SplitterContainer";
import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { SaveButton, CheckButton } from "@plannel/components/ActionIconButton";
import OriginDestLocationAutocomplete from "@plannel/components/OriginDestLocationAutocomplete";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import RpUtil from "../utils/rp-util";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { FS } from "@plannel/utils/font-util";

const { versionStatusCode } = CodeUtil;
const { SIMULATION, PLANNED } = versionStatusCode;
const { BOH, DEMAND_QTY, ARRIVAL_QTY, EOH, TARGET_STOCK, ROP, PURCHASE_PLAN_QTY, PURCHASE_PLAN_QTY_ADJ, REPLENISH_PLAN_QTY, REPLENISH_PLAN_QTY_ADJ } = CodeUtil.rpMeasure;

const { snackBarMessageColor } = ColorUtil;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
const RpWorkbench = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [10, 30, 50, 100];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? { originLocation: null, destLocation: null });
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ? reduxViewState.filter : {});
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");

  const [sortParams, setSortParams] = useState({});

  const [backDropOpen, setBackDropOpen] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const columnDefs = useRef();
  const exportColumnDefs = useRef();
  const gridHeaderMap = useRef({});
  const chartSelectedRowsRef = useRef([]); // 마지막 차트 선택 행 — 언어 변경 시 재조립용
  const [gridLoading, setGridLoading] = useState(false);
  const rpWorkbenchHeaders = useRef([]);
  const [rpWorkbenchResult, setRpWorkbenchResult] = useState([]);
  const workbenchUpdateDatas = useRef([]);

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const allMeasure = useMemo(
    () => [BOH, DEMAND_QTY, ARRIVAL_QTY, EOH, TARGET_STOCK, ROP, PURCHASE_PLAN_QTY, PURCHASE_PLAN_QTY_ADJ, REPLENISH_PLAN_QTY, REPLENISH_PLAN_QTY_ADJ],
    []
  );
  const essentialMeasure = [PURCHASE_PLAN_QTY_ADJ, REPLENISH_PLAN_QTY_ADJ];
  const rowsPerCombination = useMemo(() => { return measures?.length }, [measures]);

  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);
  const defaultTypeOptions = { strokeWidth: 1.5, barSize: 20 };
  const chartSeries = [
    { field: "purchasePlanQtyAdj", text: t("purchasePlanQtyAdj"), type: "bar", typeOptions: { ...defaultTypeOptions, stackId: "rpBar", stroke: ColorUtil.colorSet(0), fill: ColorUtil.colorSet(0) }, orientation: "left", groupName: t("qty") },
    { field: "replenishPlanQtyAdj", text: t("replenishPlanQtyAdj"), type: "bar", typeOptions: { ...defaultTypeOptions, stackId: "rpBar", stroke: ColorUtil.colorSet(1), fill: ColorUtil.colorSet(1) }, orientation: "left", groupName: t("qty") },
    { field: "demandQty", text: t("demandQty"), type: "bar", typeOptions: { ...defaultTypeOptions, fill: ColorUtil.colorSet(2), stroke: ColorUtil.colorSet(2) }, orientation: "left", groupName: t("qty") },
    { field: "boh", text: t("boh"), type: "line", typeOptions: { ...defaultTypeOptions, stroke: ColorUtil.colorSet(3), fill: ColorUtil.colorSet(3) }, orientation: "right", groupName: t("qty") },
    { field: "eoh", text: t("eoh"), type: "line", typeOptions: { ...defaultTypeOptions, stroke: ColorUtil.colorSet(4), fill: ColorUtil.colorSet(4) }, orientation: "right", groupName: t("qty") },
    { field: "targetStock", text: t("targetStock"), type: "line", typeOptions: { ...defaultTypeOptions, stroke: ColorUtil.colorSet(5), fill: ColorUtil.colorSet(5) }, orientation: "right", groupName: t("qty") }
  ];

  const pivotConfig = {
    groupKey: "measure",
    chartSeries,
    include: chartLabel,
    xAxisSortMap: gridHeaderMap.current
  };

  const cellStyleByMeasure = params => {
    if (params?.data?.measure) {
      const { measure, inhouseFlg } = params.data;
      if ((inhouseFlg && isEqual(measure, REPLENISH_PLAN_QTY_ADJ)) || (!inhouseFlg && isEqual(measure, PURCHASE_PLAN_QTY_ADJ))) {
        return { background: lightBlue[50] };
      }
    }

    return {};
  }

  const dataCellStyle = (params, headers) => {
    const cellStyle = cellStyleByMeasure(params);
    const { column, data, node, value } = params;
    if (data && column) {
      const { measure } = data;
      if (measure && column?.colId) {
        const replaceColId = column.colId.replace(GridUtils.IGNORE_P13N_COLLID, "");
        const headerIndex = headers.indexOf(replaceColId);
        if (headerIndex !== -1 && value !== null) {
          const numValue = Number(value);

          if (isEqual(measure, PURCHASE_PLAN_QTY_ADJ) || isEqual(measure, REPLENISH_PLAN_QTY_ADJ)) {
            const targetData = isEqual(measure, PURCHASE_PLAN_QTY_ADJ) ? node.data.purchasePlanQtyPivotData : node.data.replenishPlanQtyPivotData;
            const rpOrderQty = targetData[headerIndex];

            const isModified = !isEqual(numValue, Number(rpOrderQty));
            if (isModified) {
              cellStyle.color = blue.A700;
              cellStyle.fontWeight = 'bold';
            }
          }
        }
      }
    }

    return cellStyle;
  }

  const dynamicCellEditable = (params) => {
    const { measure, inhouseFlg } = params.data;

    if (measure) {
      if ((inhouseFlg && isEqual(measure, REPLENISH_PLAN_QTY_ADJ)) || (!inhouseFlg && isEqual(measure, PURCHASE_PLAN_QTY_ADJ))) {
        return true;
      }
    }

    return false;
  }

  const emptyValueFormatter = params => {
    if (isNull(params.value) || isUndefined(params.value)) {
      return "";
    }

    return NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  const defaultColumnDefs = [
    {
      headerName: 'originLocation', colId: "originLocNameGrp", width: 140, showRowGroup: 'originLocName', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'destLocation', colId: "destLocNameGrp", width: 165, showRowGroup: 'destLocName', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'category', colId: "categoryNameGrp", width: 140, showRowGroup: 'categoryName', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: { suppressCount: true },
    },
    {
      headerName: 'item', colId: "itemNameGrp", width: 220, showRowGroup: 'itemName', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: { suppressCount: true }
    },
    { headerName: "originLocation", field: "originLocName", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "destLocation", field: "destLocName", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "category", field: "categoryName", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "item", field: "itemName", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    {
      headerName: "measure", field: "measure", width: 260, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
      valueFormatter: (params) => t(params.value), headerComponent: CustomHeaderMenuIcon,
      cellStyle: cellStyleByMeasure,
      headerComponentParams: {
        enableMenu: false,
        isClicked: setMeasureModalOpen
      }
    },
  ];

  const defaultExportColumnDefs = [
    { field: "originLocName", colId: "originLocNameGrp", rowGroup: true, hide: true },
    { field: "destLocName", colId: "destLocNameGrp", rowGroup: true, hide: true },
    { field: "categoryName", colId: "categoryNameGrp", rowGroup: true, hide: true },
    { field: "itemName", colId: "itemNameGrp", rowGroup: true, hide: true },
    { headerName: "originLocation", field: "originLocName", width: 140 },
    { headerName: "destLocation", field: "destLocName", width: 160 },
    { headerName: "category", field: "categoryName", width: 140 },
    { headerName: "item", field: "itemName", width: 170 },
    {
      headerName: "measure", field: "measure", width: 200,
      valueFormatter: (params) => t(params.value)
    },
  ];

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: defaultColumnDefs })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (!isEmpty(rpVersionFilter)) {
      params.versionId = rpVersionFilter.id;
    }

    if (locationSearch?.originLocation) {
      params.originLocCd = locationSearch.originLocation.locCd;
    }

    if (locationSearch?.destLocation) {
      params.destLocCd = locationSearch.destLocation.locCd;
    }

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.hrchyCd;
    }

    if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }

      if (resultTotalCount.current) {
        params.totalCount = resultTotalCount.current;
      }
    }

    return { ...params, ...sortParams };
  }

  const setGridHeaders = (headers, headerMap) => {
    if (headers.length <= 0) {
      return;
    }

    const colDefs = cloneDeep(defaultColumnDefs);
    const exportColDefs = cloneDeep(defaultExportColumnDefs);

    headers.forEach((header) => {
      colDefs.push({
        headerName: headerMap[header], field: header, width: 110, sortable: false,
        colId: header + GridUtils.IGNORE_P13N_COLLID,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        cellStyle: params => dataCellStyle(params, headers),
        editable: dynamicCellEditable,
        cellEditor: NumericEditor,
        valueFormatter: emptyValueFormatter
      });
      
      exportColDefs.push({
        headerName: headerMap[header], field: header, width: 110,
        colId: header + GridUtils.IGNORE_P13N_COLLID,
        valueFormatter: emptyValueFormatter
      });

    });

    columnDefs.current = colDefs;
    exportColumnDefs.current = exportColDefs;
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: colDefs });
  }

  const setGridDatas = (headers, results) => {
    const datas = [];
    const targetStockQtyPivotData = "targetStockQtyPivotData";
    const reorderQtyPivotData = "reorderQtyPivotData";
    const demandQtyPivotData = "demandQtyPivotData";
    const arrivalQtyPivotData = "arrivalQtyPivotData";
    const bohQtyPivotData = "bohQtyPivotData";
    const eohQtyPivotData = "eohQtyPivotData";
    const purchasePlanQtyPivotData = "purchasePlanQtyPivotData";
    const replenishPlanQtyPivotData = "replenishPlanQtyPivotData";
    const purchasePlanQtyAdjPivotData = "purchasePlanQtyAdjPivotData";
    const replenishPlanQtyAdjPivotData = "replenishPlanQtyAdjPivotData";

    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;

      for (const measureName of measure) {
        const data = { ...resultsData, measure: measureName };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case DEMAND_QTY:
              data[header] = resultsData[demandQtyPivotData][idx];
              break;

            case ARRIVAL_QTY:
              data[header] = resultsData[arrivalQtyPivotData][idx];
              break;

            case TARGET_STOCK:
              data[header] = resultsData[targetStockQtyPivotData][idx];
              break;

            case ROP:
              data[header] = resultsData[reorderQtyPivotData][idx];
              break;

            case BOH:
              data[header] = resultsData[bohQtyPivotData][idx];
              break;

            case EOH:
              data[header] = resultsData[eohQtyPivotData][idx];
              break;

            case PURCHASE_PLAN_QTY:
              data[header] = resultsData[purchasePlanQtyPivotData][idx];
              break;

            case REPLENISH_PLAN_QTY:
              data[header] = resultsData[replenishPlanQtyPivotData][idx];
              break;

            case PURCHASE_PLAN_QTY_ADJ:
              data[header] = resultsData[purchasePlanQtyAdjPivotData][idx];
              break;

            case REPLENISH_PLAN_QTY_ADJ:
              data[header] = resultsData[replenishPlanQtyAdjPivotData][idx];
              break;

            default:
              break;
          }
        }

        datas.push(data);
      }
    }

    return datas;
  }

  const retrieveRpWorkbench = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    columnDefs.current = defaultColumnDefs;
    exportColumnDefs.current = defaultExportColumnDefs;

    rpWorkbenchService.getAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;

        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          rpWorkbenchHeaders.current = localDateHeader;
          setGridHeaders(localDateHeader, headerMap);
          setRpWorkbenchResult(setGridDatas(localDateHeader, data));
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          rpWorkbenchHeaders.current = [];
          setRpWorkbenchResult([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, currentPage, pageSize });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
      })
      .finally(() => {
        workbenchUpdateDatas.current = [];
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const updateRpResultSummary = () => {
    planVersionService.updateSummary(rpVersionFilter?.id)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_ConfirmSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmFailed") });
        debug(e);
      })
      .then(() => {
        setBackDropOpen(false);
      });
  }

  const confirmVersion = (isSaved = false) => {
    if (!isSaved) {
      setBackDropOpen(true);
    }

    rpWorkbenchService.confirmVersion(rpVersionFilter?.id)
      .then(() => {
        updateRpResultSummary();
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmFailed") });
        debug(e);
        setBackDropOpen(false);
      });
  }

  const updateWorkbenchData = (confirmationPending = false) => {
    setBackDropOpen(true);
    rpWorkbenchService.updateWorkbenchData(workbenchUpdateDatas.current)
      .then(() => {
        if (confirmationPending) {
          confirmVersion(true);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          retrieveRpWorkbench();
        }

        workbenchUpdateDatas.current = [];
      })
      .catch(e => {
        debug(e);
        const detailErrorMsgInfo = e.response?.data?.message?.message ? { content: t(e.response?.data?.message?.message) } : {};
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed"), ...detailErrorMsgInfo });
      }).then(() => {
        if (!confirmationPending) {
          setBackDropOpen(false);
        }
      });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      if (isEmpty(workbenchUpdateDatas.current)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      } else {
        updateWorkbenchData();
      }
    } else if (action === "CONFIRM") {
      confirmVersion();
    } else if (action === "SAVE_AND_CONFIRM") {
      updateWorkbenchData(true);
    }
  }

  const onClickConfirmButton = () => {
    if (isEmpty(workbenchUpdateDatas.current)) {
      setDialogInfo({ open: true, actionName: "CONFIRM", title: "confirm", content: t("MSG_RunSimulConfirm") });
    } else {
      setDialogInfo({ open: true, actionName: "SAVE_AND_CONFIRM", title: "confirm", content: t("MSG_RunSimulConfirmAfterSaving") });
    }
  }

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
  }


  const setChart = (selectedRows) => {
    const selectedDatas = [];
    const dateFormat = [];

    chartSelectedRowsRef.current = selectedRows ?? [];

    if (selectedRows && selectedRows.length <= 0) {
      setChartData([]);
      return;
    }

    for (const header of rpWorkbenchHeaders.current) {
      dateFormat.push( gridHeaderMap.current[header]);
    }

    for (const row of selectedRows) {
      const chartDatas = {};
      let inputFlag = false;

      for (const header of rpWorkbenchHeaders.current) {
        const series = [BOH, DEMAND_QTY, EOH, REPLENISH_PLAN_QTY_ADJ, PURCHASE_PLAN_QTY_ADJ, TARGET_STOCK];
        if (series.includes(row.measure)) {
          const headerName = gridHeaderMap.current[header];
          chartDatas[headerName] = row[header];
          chartDatas.measure = t(row.measure);

          inputFlag = true;
        }
      }

      if (inputFlag) {
        selectedDatas.push(chartDatas);
      }
    }
    setChartLabel(dateFormat);
    setChartData(selectedDatas);

  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    if (isEmpty(chartSelectedRowsRef.current)) {
      return;
    }
    setChart(chartSelectedRowsRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);


  const initCheck = (params) => {
    if (rpWorkbenchResult) {
      if (params.type === "componentStateChanged" && params?.rowData) {
        const selectedRows = [];
        let firstLeafNode = null;
        params.api.forEachLeafNode(node => { if (!firstLeafNode) firstLeafNode = node; });
        if (firstLeafNode) {
          firstLeafNode.setSelected(true);
          for (const leaf of firstLeafNode.parent?.allLeafChildren ?? []) {
            selectedRows.push(leaf.data);
          }
        }
        setChart(selectedRows);
      }
    }
  }

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();      

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
    }
  }

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({nodes: selected, newValue: true, source: CHECKBOX_SELECTED});
  };

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const addUpdateDatas = datas => {
    const updateDatas = cloneDeep(workbenchUpdateDatas.current);

    for (const data of datas) {
      const idx = findIndex(updateDatas, updateData => isEqual(updateData.id, data.id));

      if (idx > -1) {
        updateDatas[idx] = { ...updateDatas[idx], ...data };
      } else {
        updateDatas.push(data);
      }
    }

    workbenchUpdateDatas.current = updateDatas;
  }

  const makeUpdateData = (headerIndex, idData, rpOrderQtyAdj, bohQtyAdj, eohQtyAdj, salesQtyAdj, arrivalQtyAdj, verNumData) => {
    const updateData = {};
    updateData.id = idData[headerIndex];
    updateData.rpOrderQtyAdj = rpOrderQtyAdj[headerIndex];
    updateData.bohQtyAdj = bohQtyAdj[headerIndex];
    updateData.eohQtyAdj = eohQtyAdj[headerIndex];
    updateData.salesQtyAdj = salesQtyAdj[headerIndex];
    updateData.arrivalQtyAdj = arrivalQtyAdj[headerIndex];
    updateData.verNum = verNumData[headerIndex];

    return updateData;
  }

  const onCellValueChanged = params => {
    const { column, data, value, node } = params;
    const { measure, otdLt, moq, ioq } = data;

    if (isEqual(measure, PURCHASE_PLAN_QTY_ADJ) || isEqual(measure, REPLENISH_PLAN_QTY_ADJ)) {
      const rpOrderQtyAdj = Number(value);
      const headerText = column.colId.replace(GridUtils.IGNORE_P13N_COLLID, "");
      const changeValueHeader = findIndex(rpWorkbenchHeaders.current, header => isEqual(header, headerText));

      const idData = data.idPivotData;
      const demandDatas = data.demandQtyPivotData;
      const bohAdjDatas = data.bohQtyAdjPivotData;
      const eohAdjDatas = data.eohQtyAdjPivotData;
      const targetAdjDatas = isEqual(measure, PURCHASE_PLAN_QTY_ADJ) ? data.purchasePlanQtyAdjPivotData : data.replenishPlanQtyAdjPivotData;
      const salesAdjDatas = data.salesQtyAdjPivotData;
      const arrivalAdjDatas = data.arrivalQtyAdjPivotData;
      const verNumData = data.verNumPivotData;

      const bucket = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
      const otdLtByBucket = RpUtil.getOtdLtByBucket(bucket, otdLt);
      const modifyIdx = changeValueHeader + otdLtByBucket;

      const updateDatas = [];

      const adjustedValue = RpUtil.getRpOrderQtyConsideredMoqAndIoq(rpOrderQtyAdj, moq, ioq);
      targetAdjDatas[changeValueHeader] = adjustedValue;
      if (!isEqual(rpOrderQtyAdj, adjustedValue)) {
        node.data[headerText] = adjustedValue;
        setTimeout(() => { params.api.flashCells({ rowNodes: [node], columns: [headerText], fadeDelay: 1000 }); }, 300);
        setTimeout(() => { params.api.redrawRows({ rowNodes: [node] }); }, 200);
      }

      const firstData = makeUpdateData(changeValueHeader, idData, targetAdjDatas, bohAdjDatas, eohAdjDatas, salesAdjDatas, arrivalAdjDatas, verNumData);
      updateDatas.push(firstData);

      if (arrivalAdjDatas.length > modifyIdx) {
        arrivalAdjDatas[modifyIdx] = adjustedValue;

        for (let headerIdx = modifyIdx; headerIdx < rpWorkbenchHeaders.current.length; headerIdx++) {
          if (headerIdx !== 0) {
            bohAdjDatas[headerIdx] = eohAdjDatas[headerIdx - 1];
          }

          salesAdjDatas[headerIdx] = (bohAdjDatas[headerIdx] + arrivalAdjDatas[headerIdx]) >= demandDatas[headerIdx] ? demandDatas[headerIdx] : (bohAdjDatas[headerIdx] + arrivalAdjDatas[headerIdx]);
          eohAdjDatas[headerIdx] = Math.max(bohAdjDatas[headerIdx] - demandDatas[headerIdx] + arrivalAdjDatas[headerIdx], 0);

          const updateData = makeUpdateData(headerIdx, idData, targetAdjDatas, bohAdjDatas, eohAdjDatas, salesAdjDatas, arrivalAdjDatas, verNumData);
          updateDatas.push(updateData);
        }
      }

      addUpdateDatas(updateDatas);

      node.setSelected(false, true, CHECKBOX_SELECTED);
      node.setSelected(true, false, CHECKBOX_SELECTED);
    }
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveRpWorkbench();
    }
  }

  useEffect(() => {
    if (gridLoading && rpVersionFilter && !isEmpty(measures)) {
      retrieveRpWorkbench();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, measures, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading && rpVersionFilter && !isEmpty(measures)) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter, locationSearch.originLocation?.id, locationSearch.destLocation?.id, filterValue?.category]);

  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED]} />
        <OriginDestLocationAutocomplete viewName={viewName} onChangeFilter={setLocationSearch} key={"originDestLocation"}/>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} />
        <RPItemAutocomplete retrieve={initPageToRetrieve} sx={{ mr: 1.5 }} searchValue={itemSearch} key={"ITEM"} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton
            title={title}
            service={rpWorkbenchService}
            setExcelData={setGridDatas}
            columnDefs={exportColumnDefs.current}
            hasUnsavedChanges={!isEmpty(workbenchUpdateDatas.current)}
            totalRows={resultTotalCount.current * rowsPerCombination}
            params={{ ...getRequestParams(false), measureCount: rowsPerCombination }}
          />
          <SaveButton disabled={!rpVersionFilter} onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <CheckButton label="confirm" disabled={!rpVersionFilter} onClick={onClickConfirmButton} />
        </Box>
      </FilterContainer>
      <Box className={"grid-area"}>
        <SplitterContainer>
          <Box sx={{ height: "100%" }}>
            <Box id="rp-workbench-grid" className="ag-theme-balham non-background-selected-row" mb={1} sx={{ height: "calc(100% - 50px)" }}>
              <AgGridReact
                ref={gridRef}
                rowData={rpWorkbenchResult}
                {...defaultGridMemo}
                groupDisplayType={'custom'}
                groupDefaultExpanded={-1}
                groupHideOpenParents
                groupSelectsChildren
                onRowSelected={onRowSelected}
                onCellClicked={onCellGridClicked}
                onComponentStateChanged={initCheck}
                onGridReady={onGridReady}
                onCellValueChanged={onCellValueChanged}
                onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
              >
              </AgGridReact>
            </Box>
            <PaginationContainer
              currentPage={currentPage} totalPages={gridPages}
              pageSize={pageSize} pageSizes={pageSizes}
              setCurrentPage={setCurrentPage} setPageSize={setPageSize}
            />
          </Box>
          <Box id="rp-workbench-chart" className="chart-grid-area chart-area" sx={{ height: "100%" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: FS.SUB } }}
              nullConvert
            />
          </Box>
        </SplitterContainer>
      </Box> 
      <Backdrop sx={{ color: '#fff', zIndex: 1400 }} open={backDropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        requiredMeasure={essentialMeasure}
        applyMeasures={applyMeasures}
      />
    </>
  );
};

export default withTranslation()(RpWorkbench);